/**
 * naturePlacement — pure, deterministic, seeded placement math for the Tree &
 * Nature system. Given a biome's vegetation profile + a level's standable
 * platforms + a seed, resolves the exact set of plants (kind, position, size,
 * tint, flip) that the renderer mints into ISprite handles.
 *
 * Pure + seedable (lift-ready checklist §3): same `(profile, platforms, seed)`
 * ⇒ identical placements every run — no wall-clock, no global RNG, no renderer
 * deps. Unit-testable with fakes. Reuses the determinism kit (`seededRand` /
 * `hashSeed` from atmospherePlacement) so it shares the same sin-hash RNG as
 * every other visual lift — match `seededRand` for cross-codebase parity.
 *
 * Placement model: each standable platform gets a count of plants proportional
 * to its width × the biome density (scaled by the live density multiplier).
 * Each plant's kind is drawn by weighted random from the biome's kind mix; its
 * x is scattered along the platform top; its size/tint/flip are seeded per
 * plant. Trees get wider spacing (they're big); grass clusters tightly.
 */
import { seededRand, hashSeed } from '../atmosphere/atmospherePlacement';
import { lerp } from '../../../shared/utils/MathUtils';
import type { BiomeVegetation, VegetationKind, VegetationSpec } from './natureConfig';
import type { NatureLiveConfig } from './natureLiveConfig';
import type { SceneNatureProp } from '../../../shared/SceneTypes';
import { validateTownPropAttachments, type TownMountWorld } from '../../../shared/scene/townPropAttachments';
import { isPropKind } from './propTextures';
import { TOWN_PROP_TEMPLATES, resolveTownPropTemplate, townPropPalette, townTemplateTextureSuffix, type TownPropTemplate, type TownPropPalette, type TownPropRenderBand } from './props/townPropTemplates';

/** Minimal platform shape the resolver needs (keeps it decoupled from Platform). */
export interface PlacementPlatform {
  id?: string;
  x: number;
  y: number;       // TOP edge (the standing surface — see LEVEL_FORMATS_EXPLAINED.md)
  width: number;
  type: string;    // 'normal' | 'moving' | 'falling' | 'bouncy' | 'ice' | …
  properties?: Record<string, unknown>;
}

/** A fully-resolved plant — the renderer mints one ISprite per entry. */
export interface PlantedVegetation {
  kind: VegetationKind;
  /** World X (center of the plant's base). */
  x: number;
  /** World Y (the platform's top edge — the plant's base sits here). */
  y: number;
  /** Display width (px). */
  width: number;
  /** Display height (px). */
  height: number;
  /** Foliage tint (0xRRGGBB). */
  tint: number;
  /** Flip horizontally (for variety — trees/bushes are asymmetric silhouettes). */
  flipX: boolean;
  /** Base alpha (0..1). */
  alpha: number;
  /** Texture-sheet key (`nature_<kind>_<biome>` — the renderer loads/generates it). */
  textureKey: string;
  template?: TownPropTemplate;
  palette?: TownPropPalette;
  /** Fixed by the selected template, never by an arbitrary authored z-index. */
  renderBand?: TownPropRenderBand;
  /** Z-depth (Z_LAYERS.VEGETATION; included so the resolver is renderer-complete). */
  depth: number;
}

/** The Z-depth vegetation renders at (kept here so the pure resolver is complete
 *  without importing Z_LAYERS — the renderer-seam contract in SPEC.md §5). */
export const VEGETATION_DEPTH = 3;
export const ARCHITECTURE_DEPTH = -10;
export const ARCHITECTURE_FIXTURE_DEPTH = -9;

/** Platform types that are NOT standable surfaces for vegetation. */
const NON_STANDABLE_TYPES = new Set(['teleporter', 'rope', 'decorative', 'cover']);

/** Is this platform a surface vegetation can be planted on? */
export function isPlantable(platform: PlacementPlatform): boolean {
  if (NON_STANDABLE_TYPES.has(platform.type)) return false;
  if (platform.properties?.nature === false) return false;
  // Bounding walls (the `wall_` id prefix) are collision, not ground.
  if (platform.id?.startsWith('wall_')) return false;
  return true;
}

function parseAuthoredTint(value: number | string): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.min(0xffffff, Math.round(value)));
  }
  if (typeof value !== 'string' || !/^#[0-9a-f]{6}$/i.test(value)) return null;
  return Number.parseInt(value.slice(1), 16);
}

/** Resolve explicit scene landmarks through the same sprite contract as seeded
 * vegetation. Invalid author data is ignored rather than taking down a scene;
 * content validation/type-checking remains the author-facing failure surface. */
export function placeAuthoredNature(
  props: ReadonlyArray<SceneNatureProp>,
  biome: string,
  supportWorld?: TownMountWorld,
): PlantedVegetation[] {
  const out: PlantedVegetation[] = [];
  const unsupported = new Set(supportWorld ? validateTownPropAttachments(props, supportWorld).map(error => error.index) : []);
  for (const [index, prop] of props.entries()) {
    if (unsupported.has(index)) continue;
    const tint = parseAuthoredTint(prop.tint);
    if (!isPropKind(prop.kind) || tint === null) continue;
    if (![prop.x, prop.y, prop.width, prop.height].every(Number.isFinite)) continue;
    if (prop.width <= 0 || prop.height <= 0) continue;
    const template = resolveTownPropTemplate(prop.kind, prop.template);
    const palette = template ? townPropPalette(tint, prop.palette) : undefined;
    const renderBand = template ? TOWN_PROP_TEMPLATES[template].band : 'vegetation';
    const metadata = template ? TOWN_PROP_TEMPLATES[template] : undefined;
    const fixture = metadata && 'fixture' in metadata && metadata.fixture;
    out.push({
      kind: prop.kind,
      x: prop.x,
      y: prop.y,
      width: prop.width,
      height: prop.height,
      tint,
      flipX: prop.flipX ?? false,
      alpha: Math.max(0, Math.min(1, prop.alpha ?? 1)),
      // Legacy keys remain unchanged. Named art appends its template and full
      // resolved palette; dimensions stay on the sprite, so identical art shares
      // a texture even when two authors use different display sizes.
      textureKey: `nature_${prop.kind}_${biome}_${(palette?.primary ?? tint).toString(16).padStart(6, '0')}${template ? townTemplateTextureSuffix(template, palette!) : ''}`,
      ...(template ? { template, palette, renderBand } : {}),
      depth: fixture ? ARCHITECTURE_FIXTURE_DEPTH : renderBand === 'architecture' ? ARCHITECTURE_DEPTH : VEGETATION_DEPTH,
    });
  }
  return out;
}

/** Aspect ratio (width / height) per kind — trees are tall, grass is wide+short.
 *  Prop aspects match their intrinsic SVG dimensions (propTextures.ts) so the
 *  sprites scale without distortion. */
const KIND_ASPECT: Record<VegetationKind, number> = {
  sign: 72 / 44,
  tree: 0.6,   // height × 0.6 = width (a tall canopy)
  bush: 1.2,   // height × 1.2 = width (a round low bush)
  grass: 2.0,  // height × 2.0 = width (a wide grass tuft)
  palm_tree: 0.75,  // tall bent trunk + frond crown (90×120)
  beach_rock: 1.6,  // low wide rounded cluster (64×40)
  tide_pool: 3.0,   // flat ellipse (96×32)
  trinket: 1.0,     // tiny square-ish quest prop (24×24)
  pig_pen: 2.4,     // low wide fence (110×46)
  village_house: 0.9,  // peaked-roof cottage (90×100)
  village_well: 0.8,   // stone well + A-frame roof (64×80)
  lantern_post: 0.5,   // tall thin post + glow halo (56×112)
  dock: 2.5,           // plank deck + pilings (140×56)
  autumn_leaf: 1.0,    // tiny drifting leaf (20×20)
  dead_bracken: 0.786, // angular twisted bush, no foliage (44×56)
  moor_stone: 1.381,   // angular weathered stone (58×42)
  mist_shelf: 3.125,   // flat pooled-fog ledge (100×32)
  fallen_log: 3.158,   // long horizontal log (120×38)
  gate: 0.739,         // fence-post landmark (68×92)
  journal_page: 2.143, // a page lying flat under a stone (60×28)
  colonnade_column: 0.329,      // tall leaning interior support (46×140)
  broken_pillar: 2.0,           // snapped stub + toppled top-half (112×56)
  dais: 2.917,                  // raised circular boss platform (140×48)
  spiral_ledge: 3.125,          // curved spiral-staircase edge (100×32)
  bioluminescent_fungus: 1.3,   // tiny glowing wall cluster (26×20)
  gnarled_tree: 0.75,            // broad bare woodland landmark (120×160)
};

/** Minimum spacing per kind (px) — trees need room, grass can cluster. */
const KIND_MIN_SPACING: Record<VegetationKind, number> = {
  sign: 100,
  tree: 80,
  bush: 40,
  grass: 20,
  palm_tree: 90,   // landmark silhouette — never a palm wall
  beach_rock: 44,
  tide_pool: 110,  // rare accents, spread out
  trinket: 22,     // tiny — may cluster in the tide-pool area
  pig_pen: 150,    // marks distinct spawn areas (west/east pens)
  village_house: 140,  // 4-5 houses form a cluster, never a rowhouse wall
  village_well: 240,   // THE village-square centerpiece — one per area
  lantern_post: 160,   // 2-3 spread glow pools ("held breath" spacing)
  dock: 260,           // a shoreline landmark, not a boardwalk
  autumn_leaf: 16,     // tiny — drifts cluster naturally
  dead_bracken: 50,    // 8-10 clusters — the biome namesake, may scatter freely
  moor_stone: 70,      // jump platforms — need room
  mist_shelf: 200,     // rare spooky-onset ledges, ≤4 per spec
  fallen_log: 180,     // jump-platform landmark
  gate: 500,           // the ONE tidewend↔brackenmoor threshold — never repeats
  journal_page: 220,   // 3 quest ledges, spread across the map
  colonnade_column: 70,   // lines the interior walls — a colonnade ROW, not one-off
  broken_pillar: 350,     // rare arena cover — 2 per map
  dais: 550,               // THE single boss pedestal — never repeats
  spiral_ledge: 220,       // rare spiral-descent drop-ins, ≤4-5 per spec
  bioluminescent_fungus: 45, // small wall clusters — may bunch up (4-6 clusters)
  gnarled_tree: 500,        // authored landmark only; never a random tree wall
};

/** Draw a weighted-random kind from the biome's kind mix. Pure (seeded). */
function pickKind(kinds: Partial<Record<VegetationKind, VegetationSpec>>, seed: number): VegetationKind | null {
  const entries = (Object.entries(kinds) as [VegetationKind, VegetationSpec][])
    .filter(([, spec]) => spec.weight > 0);
  if (entries.length === 0) return null;
  const total = entries.reduce((s, [, spec]) => s + spec.weight, 0);
  const r = seededRand(seed) * total;
  let acc = 0;
  for (const [kind, spec] of entries) {
    acc += spec.weight;
    if (r < acc) return kind;
  }
  return entries[entries.length - 1][0];
}

/**
 * Resolve the full set of plants for a biome's vegetation profile across a
 * level's platforms. Pure + deterministic given the seed.
 *
 * @param profile   the biome's resolved vegetation profile (from resolveBiomeVegetation)
 * @param platforms the level's platforms (only standable ones are planted)
 * @param seed      the base seed (typically hashSeed(levelId))
 * @param biome     the biome id (for texture keys)
 * @param live      the live-tunable multipliers (density/scale/alpha)
 */
export function placeVegetation(
  profile: BiomeVegetation,
  platforms: ReadonlyArray<PlacementPlatform>,
  seed: number,
  biome: string,
  live: NatureLiveConfig,
): PlantedVegetation[] {
  const out: PlantedVegetation[] = [];
  const densityMul = live.densityMultiplier;
  const scaleMul = live.scaleMultiplier;
  const alphaMul = live.alphaMultiplier;

  let plantSeed = seed;
  for (let pi = 0; pi < platforms.length; pi += 1) {
    const p = platforms[pi];
    if (!isPlantable(p)) continue;
    // Expected plant count = width × density × live multiplier (density is per
    // 100px). Stochastic rounding: floor + a seeded fractional chance for one
    // more, so narrow platforms still sometimes get a plant.
    const expected = (p.width / 100) * profile.density * densityMul;
    const count = Math.floor(expected) + (seededRand(plantSeed) < (expected - Math.floor(expected)) ? 1 : 0);
    if (count <= 0) continue;

    // Scatter `count` plants along the platform top, respecting min spacing.
    // Simple greedy: pick a random x, reject if too close to an existing one
    // (a few tries), then place. Deterministic via seed.
    const placedX: { x: number; kind: VegetationKind; spacing: number }[] = [];
    for (let i = 0; i < count; i += 1) {
      plantSeed += 7.31;
      const kind = pickKind(profile.kinds, plantSeed);
      if (!kind) continue;
      const spec = profile.kinds[kind]!;
      const minSpacing = KIND_MIN_SPACING[kind];
      let chosenX = -1;
      for (let attempt = 0; attempt < 6; attempt += 1) {
        plantSeed += 1.7;
        const margin = Math.min(minSpacing / 2, p.width / 4);
        const candidateX = p.x + margin + seededRand(plantSeed) * (p.width - margin * 2);
        const tooClose = placedX.some(
          (q) => Math.abs(q.x - candidateX) < Math.max(minSpacing, q.spacing),
        );
        if (!tooClose) { chosenX = candidateX; break; }
      }
      if (chosenX < 0) continue; // couldn't fit — skip, never overlap

      plantSeed += 2.3;
      const height = lerp(spec.sizeMin, spec.sizeMax, seededRand(plantSeed)) * scaleMul;
      const width = height * KIND_ASPECT[kind];
      plantSeed += 3.1;
      const tint = spec.palette[Math.floor(seededRand(plantSeed) * spec.palette.length) % spec.palette.length];
      plantSeed += 4.9;
      const flipX = seededRand(plantSeed) > 0.5;

      out.push({
        kind,
        x: chosenX,
        y: p.y, // platform top edge = the plant's base
        width,
        height,
        tint,
        flipX,
        alpha: spec.alpha * alphaMul,
        textureKey: `nature_${kind}_${biome}_${tint.toString(16).padStart(6, '0')}`,
        depth: VEGETATION_DEPTH,
      });
      placedX.push({ x: chosenX, kind, spacing: minSpacing });
    }
  }
  return out;
}

/** Convenience: resolve placements for a level id (hashes the id to a seed). */
export function placeVegetationForLevel(
  profile: BiomeVegetation,
  platforms: ReadonlyArray<PlacementPlatform>,
  levelId: string,
  biome: string,
  live: NatureLiveConfig,
): PlantedVegetation[] {
  return placeVegetation(profile, platforms, hashSeed(levelId), biome, live);
}
